'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'LLM prompt injection & jailbreaking',
    description:
      'Direct and indirect prompt injection attacks against large language models. Jailbreak attempts, system prompt extraction, and instruction override testing. Covers both hosted APIs (OpenAI, Anthropic, Gemini) and self-hosted models.',
  },
  {
    tag: '[02]',
    title: 'RAG and retrieval pipeline attacks',
    description:
      'Security testing of retrieval-augmented generation systems, data poisoning, context manipulation, unauthorised data retrieval, and cross-tenant data leakage in multi-tenant RAG architectures.',
  },
  {
    tag: '[03]',
    title: 'Model extraction and inversion',
    description:
      'Attempts to extract model weights, training data, or proprietary system prompts through repeated querying. Membership inference attacks to determine whether specific data was used in training.',
  },
  {
    tag: '[04]',
    title: 'Agentic AI and tool-use security',
    description:
      'Security testing of AI agents with tool access, code execution, web browsing, file system access, and API calls. Privilege escalation through agent chains, unintended action execution, and sandbox escape.',
  },
  {
    tag: '[05]',
    title: 'AI supply chain and model integrity',
    description:
      'Assessment of model provenance, fine-tuning pipeline security, and third-party model risks. Backdoor detection in fine-tuned models and evaluation of model cards and documentation for security gaps.',
  },
  {
    tag: '[06]',
    title: 'AI application security review',
    description:
      'Full security review of applications built on AI, covering the traditional application attack surface (auth, injection, data exposure) plus AI-specific risks including output handling, trust boundaries, and human-in-the-loop bypass.',
  },
];

const outcomes = [
  { metric: 'OWASP', label: 'LLM Top 10 aligned testing methodology' },
  { metric: 'Agentic', label: 'Coverage of AI agents, RAG, and tool-use systems' },
  { metric: 'Novel', label: 'Attack research beyond standard checklists' },
  { metric: 'Actionable', label: 'Developer-ready remediation guidance' },
];

const methodology = [
  {
    phase: '01',
    title: 'AI system mapping',
    desc: 'Map the AI system architecture, models, APIs, data pipelines, agent chains, tool integrations, and trust boundaries. Identify the highest-risk attack surfaces.',
  },
  {
    phase: '02',
    title: 'Threat modelling',
    desc: 'AI-specific threat modelling covering adversarial inputs, data poisoning, model extraction, and agentic attack paths. Prioritised by business impact.',
  },
  {
    phase: '03',
    title: 'Adversarial testing',
    desc: 'Manual adversarial testing by engineers with deep AI security expertise. Novel attack chains, not just standard prompt injection checklists.',
  },
  {
    phase: '04',
    title: 'Reporting & guidance',
    desc: 'Technical findings with developer-ready remediation guidance. Executive summary covering AI-specific risk posture and recommended controls.',
  },
];

const techStack = [
  { category: 'LLM Testing', tools: ['Garak', 'PyRIT', 'PromptBench', 'LLM-Fuzzer'] },
  { category: 'Adversarial ML', tools: ['ART (IBM)', 'Foolbox', 'CleverHans', 'TextAttack'] },
  { category: 'Model Analysis', tools: ['Hugging Face tools', 'ONNX inspection', 'Custom extraction scripts'] },
  { category: 'Frameworks', tools: ['OWASP LLM Top 10', 'MITRE ATLAS', 'NIST AI RMF', 'EU AI Act'] },
  { category: 'RAG & Agents', tools: ['LangChain security review', 'LlamaIndex audit', 'Custom agent harnesses'] },
  { category: 'Traditional AppSec', tools: ['Burp Suite Pro', 'OWASP ASVS', 'API security testing'] },
];

const differentiators = [
  { title: 'AI security specialists', desc: 'Our testers have deep expertise in both traditional application security and AI/ML systems, not generalists with a prompt injection checklist.' },
  { title: 'Novel attack research', desc: 'We conduct original research into AI attack techniques. Our engagements go beyond published checklists to find the vulnerabilities others miss.' },
  { title: 'Full stack coverage', desc: 'We test the AI layer and the application layer. Most AI pentests ignore the traditional attack surface, we cover both.' },
  { title: 'Compliance-ready', desc: 'Reports aligned to OWASP LLM Top 10, MITRE ATLAS, NIST AI RMF, and EU AI Act requirements.' },
];

export default function AIPentestingPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', right: '5%', opacity: 0.35 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '-80px', left: '-50px', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services/pentesting" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Penetration Testing</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">AI Pentesting</span>
          </div>

          <div className="max-w-[900px]">
            <div className="flex items-center gap-3 mb-4 fade-in-up" style={{ transitionDelay: '0.03s' }}>
              <span className="font-mono-brand text-[0.6rem] text-[#80edfa] border border-[#80edfa]/40 px-2 py-0.5 uppercase tracking-widest">Specialisation</span>
            </div>
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [AI Penetration Testing]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Security testing built<br />
              <span className="text-[#80edfa]">for AI systems.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              We specialise in adversarial security testing of AI and ML systems, LLM prompt injection, RAG pipeline attacks, model extraction, agentic AI exploitation, and AI supply chain security. Purpose-built for organisations building or deploying AI.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes strip */}
      <section className="border-t border-[#222] bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-heading text-3xl lg:text-4xl text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="text-sm text-[#555] leading-snug">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Attack coverage</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">What we test</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Methodology</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">How we approach AI testing</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {methodology?.map((p, i) => (
              <div key={i} className="bg-[#0a0f14] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[{p?.phase}]</p>
                <h3 className="font-heading text-lg text-[#f5f5f8] font-medium mb-3">{p?.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{p?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Tooling & frameworks</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">What we use</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {techStack?.map((t, i) => (
              <div key={i} className="bg-[#050a0f] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.6rem] text-[#555] uppercase tracking-widest mb-4">{t?.category}</p>
                <div className="flex flex-wrap gap-2">
                  {t?.tools?.map((tool) => (
                    <span key={tool} className="font-mono-brand text-[0.65rem] text-[#aaa] border border-[#222] px-3 py-1.5 uppercase tracking-wide">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Why TechSonar</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">What makes this different</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {differentiators?.map((d, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="w-8 h-px bg-[#80edfa] mb-4" />
                <h3 className="font-heading text-lg text-[#f5f5f8] font-medium mb-2">{d?.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{d?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Get started]</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium mb-6">
              Building or deploying AI? Let us test it.
            </h2>
            <p className="text-[#666] text-lg mb-8 leading-relaxed">
              Talk to us about your AI system, whether it is an LLM-powered product, an agentic workflow, or a RAG pipeline. We will scope an engagement that gives you real confidence in its security.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="btn-primary">Book a discovery call</Link>
              <Link href="/services/pentesting" className="btn-secondary">View all pentesting services</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
